import {
  assert,
  assertNever,
  BaseNumber,
  CheckedAddress,
  NormalizedNumber,
  UnixDuration,
} from '@sparkdotfi/common-universal'
import { QueryKey } from '@tanstack/react-query'
import { Address, erc4626Abi } from 'viem'
import { gnosis } from 'viem/chains'

import { DepositToSavingsAction } from '../types'
import { formatMinAmountOutForPsm3 } from './formatMinAmountOutForPsm3'
import { getSavingsDepositActionPath } from './getSavingsDepositActionPath'
import { susdsAbi } from '@/config/abis/susdsAbi'
import { SPARK_UI_REFERRAL_CODE, SPARK_UI_STUSDS_REFERRAL_CODE } from '@/config/consts'
import {
  migrationActionsConfig,
  psm3Abi,
  psm3Address,
  psmActionsConfig,
  savingsXDaiAdapterAbi,
  savingsXDaiAdapterAddress,
  sparkVaultV2Abi,
  usdcVaultAbi,
  usdsPsmActionsConfig,
} from '@/config/contracts-generated'
import { trackTransactionConfirmation } from '@/domain/analytics/cookie3'
import { trackEvent } from '@/domain/analytics/mixpanel'
import { getContractAddress } from '@/domain/hooks/useContractAddress'
import { Token } from '@/domain/types/Token'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { getBalancesQueryKeyPrefix } from '@/domain/wallet/getBalancesQueryKeyPrefix'
import { allowanceQueryKey } from '@/features/actions/flavours/approve/logic/query'
import { ensureConfigTypes } from '@/features/actions/logic/transactions/useWrite'
import { ActionConfig, ActionContext, GetWriteConfigResult } from '@/features/actions/logic/types'
import { calculateGemConversionFactor } from '@/features/actions/utils/savings'
import { stusdsDepositDataQueryKey } from '@/features/savings/dialogs/deposit/logic/stusds/stusdsDepositDataQuery'
import { savingsV2DepositDataQueryKey } from '@/features/savings/dialogs/deposit/logic/v2/savingsV2DepositDataQuery'
import { stusdsWithdrawDataQueryKey } from '@/features/savings/dialogs/withdraw/logic/stusds/stusdsWithdrawDataQuery'
import { savingsV2WithdrawDataQueryKey } from '@/features/savings/dialogs/withdraw/logic/v2/savingsV2WithdrawDataQuery'
import { SavingsConverter } from '@/features/savings/logic/savings-converters/types'

export function createDepositToSavingsActionConfig(
  action: DepositToSavingsAction,
  context: ActionContext,
): ActionConfig {
  const { account, chainId } = context
  const actionPath = getSavingsDepositActionPath({
    token: action.token,
    savingsToken: action.savingsToken,
    chainId,
  })

  const savingsReferralCode = context.integratorRefCode ?? SPARK_UI_REFERRAL_CODE

  return {
    getWriteConfig: () => {
      const { token, savingsToken } = action
      const assetsAmount = token.toBigInt(action.value)

      switch (actionPath) {
        case 'usdc-to-spusdc':
        case 'usdt-to-spusdt':
        case 'eth-to-speth':
        case 'weth-to-speth':
        case 'pyusd-to-sppyusd':
        case 'avalanche-usdc-to-spusdc':
          return ensureConfigTypes({
            address: savingsToken.address,
            abi: sparkVaultV2Abi,
            functionName: 'deposit',
            args: [assetsAmount, account, savingsReferralCode],
          })

        case 'usds-to-susds':
          return ensureConfigTypes({
            address: savingsToken.address,
            abi: susdsAbi,
            functionName: 'deposit',
            args: [assetsAmount, account, savingsReferralCode],
          })
        case 'usds-to-stusds':
          return ensureConfigTypes({
            address: savingsToken.address,
            abi: susdsAbi,
            functionName: 'deposit',
            args: [assetsAmount, account, SPARK_UI_STUSDS_REFERRAL_CODE],
          })
        case 'dai-to-sdai':
          return ensureConfigTypes({
            address: savingsToken.address,
            abi: erc4626Abi,
            functionName: 'deposit',
            args: [assetsAmount, account],
          })

        case 'usdc-to-susdc': {
          assert(
            context.savingsAccounts,
            'Savings account repository info is required for usdc deposit to savings action',
          )
          const savingsConverter = context.savingsAccounts.findOneBySavingsToken(savingsToken).converter

          const minAmountOut = savingsToken.toBigInt(
            calculateMinSharesAmountOut({
              savingsConverter,
              savingsToken,
              amountIn: action.value,
            }),
          )

          return ensureConfigTypes({
            address: savingsToken.address,
            abi: usdcVaultAbi,
            functionName: 'deposit',
            args: [assetsAmount, account, minAmountOut, savingsReferralCode],
          })
        }

        case 'base-usdc-to-susdc':
        case 'arbitrum-usdc-to-susdc':
        case 'optimism-usdc-to-susdc':
        case 'unichain-usdc-to-susdc': {
          assert(
            context.savingsAccounts,
            'Savings account repository info is required for usdc deposit to savings action',
          )
          const savingsConverter = context.savingsAccounts.findOneBySavingsToken(savingsToken).converter

          const minAmountOut = calculateMinSharesAmountOut({
            savingsConverter,
            savingsToken,
            amountIn: action.value,
          })
          const minAmountOutFormatted = formatMinAmountOutForPsm3({
            susds: savingsToken,
            susdsAmount: minAmountOut,
            assetIn: token,
          })

          return ensureConfigTypes({
            address: savingsToken.address,
            abi: usdcVaultAbi,
            functionName: 'deposit',
            args: [assetsAmount, account, minAmountOutFormatted, savingsReferralCode],
          })
        }

        case 'dai-to-susds':
          return ensureConfigTypes({
            address: getContractAddress(migrationActionsConfig.address, chainId),
            abi: migrationActionsConfig.abi,
            functionName: 'migrateDAIToSUSDS',
            args: [account, assetsAmount],
          })

        case 'usdc-to-susds':
          return getUsdcDepositConfig({
            psmActionsAddress: getContractAddress(usdsPsmActionsConfig.address, chainId),
            token,
            savingsToken,
            actionValue: action.value,
            account,
            assetsAmount,
          })

        case 'sexy-dai-to-sdai':
          return ensureConfigTypes({
            address: savingsXDaiAdapterAddress[gnosis.id],
            abi: savingsXDaiAdapterAbi,
            functionName: 'depositXDAI',
            args: [account],
            value: assetsAmount,
          })

        case 'base-usds-to-susds':
        case 'base-usdc-to-susds':
        case 'arbitrum-usds-to-susds':
        case 'arbitrum-usdc-to-susds':
        case 'optimism-usds-to-susds':
        case 'optimism-usdc-to-susds':
        case 'unichain-usds-to-susds':
        case 'unichain-usdc-to-susds': {
          assert(
            context.savingsAccounts,
            'Savings accounts repository is required for psm withdraw from savings action',
          )
          const savingsConverter = context.savingsAccounts.findOneBySavingsToken(savingsToken).converter

          const minAmountOut = calculateMinSharesAmountOut({
            savingsConverter,
            savingsToken,
            amountIn: action.value,
          })

          const minAmountOutFormatted = formatMinAmountOutForPsm3({
            susds: savingsToken,
            susdsAmount: minAmountOut,
            assetIn: token,
          })

          return ensureConfigTypes({
            address: getContractAddress(psm3Address, chainId),
            abi: psm3Abi,
            functionName: 'swapExactIn',
            args: [
              token.address,
              savingsToken.address,
              assetsAmount,
              minAmountOutFormatted,
              account,
              BigInt(savingsReferralCode),
            ],
          })
        }

        default:
          assertNever(actionPath)
      }
    },

    invalidates: () => {
      const balancesQueryKeyPrefix = getBalancesQueryKeyPrefix({ chainId, account })
      function getAllowanceQueryKey(spender: CheckedAddress): QueryKey {
        return allowanceQueryKey({ token: action.token.address, spender, account, chainId })
      }

      switch (actionPath) {
        case 'dai-to-sdai':
        case 'usds-to-susds':
        case 'usdc-to-susdc':
        case 'base-usdc-to-susdc':
        case 'arbitrum-usdc-to-susdc':
        case 'optimism-usdc-to-susdc':
        case 'unichain-usdc-to-susdc':
          return [balancesQueryKeyPrefix, getAllowanceQueryKey(action.savingsToken.address)]

        case 'usdc-to-spusdc':
        case 'usdt-to-spusdt':
        case 'weth-to-speth':
        case 'eth-to-speth':
        case 'pyusd-to-sppyusd':
        case 'avalanche-usdc-to-spusdc':
          return [
            balancesQueryKeyPrefix,
            getAllowanceQueryKey(action.savingsToken.address),
            savingsV2DepositDataQueryKey({ chainId, savingsToken: action.savingsToken }),
            savingsV2WithdrawDataQueryKey({ chainId, savingsToken: action.savingsToken }),
          ]

        case 'usds-to-stusds':
          return [
            balancesQueryKeyPrefix,
            getAllowanceQueryKey(action.savingsToken.address),
            stusdsDepositDataQueryKey({ chainId }),
            stusdsWithdrawDataQueryKey({ chainId }),
          ]

        case 'sexy-dai-to-sdai':
          return [balancesQueryKeyPrefix]
        case 'usdc-to-susds':
          return [
            balancesQueryKeyPrefix,
            getAllowanceQueryKey(getContractAddress(usdsPsmActionsConfig.address, chainId)),
          ]
        case 'dai-to-susds':
          return [
            balancesQueryKeyPrefix,
            getAllowanceQueryKey(getContractAddress(migrationActionsConfig.address, chainId)),
          ]

        case 'base-usds-to-susds':
        case 'base-usdc-to-susds':
        case 'arbitrum-usds-to-susds':
        case 'arbitrum-usdc-to-susds':
        case 'optimism-usds-to-susds':
        case 'optimism-usdc-to-susds':
        case 'unichain-usds-to-susds':
        case 'unichain-usdc-to-susds':
          return [balancesQueryKeyPrefix, getAllowanceQueryKey(getContractAddress(psm3Address, chainId))]

        default:
          assertNever(actionPath)
      }
    },

    onSuccessfulAction: () => {
      trackEvent(actionPath, {
        walletType: context.walletType,
        savingsToken: action.savingsToken.symbol,
        underlyingToken: action.token.symbol,
        amount: action.value.toNumber(),
        chainId,
        tag: 'Savings_Conversion',
      })

      if (action.token.symbol !== TokenSymbol('stUSDS')) {
        trackTransactionConfirmation({
          name: 'Savings_Deposit_Confirmation',
          value: action.token.toUSD(action.value).toNumber(),
          isInSandbox: Boolean(context.isInSandbox),
        })
      }
    },
  }
}

interface GetUsdcDepositConfigParams {
  psmActionsAddress: CheckedAddress
  token: Token
  savingsToken: Token
  actionValue: NormalizedNumber
  account: Address
  assetsAmount: bigint
}

function getUsdcDepositConfig({
  psmActionsAddress,
  token,
  savingsToken,
  actionValue,
  account,
  assetsAmount,
}: GetUsdcDepositConfigParams): GetWriteConfigResult {
  const gemConversionFactor = calculateGemConversionFactor({
    gemDecimals: token.decimals,
    assetsTokenDecimals: savingsToken.decimals,
  })
  const assetsMinAmountOut = token.toBaseNumber(actionValue).times(BaseNumber(gemConversionFactor)).toBigInt()

  return ensureConfigTypes({
    address: psmActionsAddress,
    abi: psmActionsConfig.abi,
    functionName: 'swapAndDeposit',
    args: [account, assetsAmount, assetsMinAmountOut],
  })
}

interface CalculateMinSharesAmountOutParams {
  savingsConverter: SavingsConverter
  savingsToken: Token
  amountIn: NormalizedNumber
}
function calculateMinSharesAmountOut({
  savingsConverter,
  amountIn,
}: CalculateMinSharesAmountOutParams): NormalizedNumber {
  const currentTimestamp = savingsConverter.currentTimestamp
  // We don't know when the block with transaction will be mined so
  // we calculate the minimal amount of sUSDS to receive as the amount
  // the user would receive in 7 days
  const minimalSharesAmount = savingsConverter.predictSharesAmount({
    assets: amountIn, // we pass NormalizedNumber, so decimals don't matter
    timestamp: currentTimestamp + Number(UnixDuration({ days: 7 }).toSeconds().valueOf()),
  })

  return minimalSharesAmount
}
